/**
 * Places menu behaviour.
 *
 * The tree itself is rendered server-side by snippets/location-mega-menu.liquid,
 * so this is pure enhancement: it switches region panels, and filters the
 * destinations from a typeahead built out of the links already in the DOM.
 * Nothing here is required for the links to work — without JS every
 * destination is still a plain anchor, which is also what crawlers see.
 */
class LocationMegaMenu extends HTMLElement {
  connectedCallback() {
    this.variant = this.dataset.variant || 'desktop';

    this.searchInput = this.querySelector('[data-loc-search]');
    this.resultsEl = this.querySelector('[data-loc-results]');
    this.panelsEl = this.querySelector('[data-loc-panels]');
    this.panels = Array.from(this.querySelectorAll('[data-loc-panel]'));
    this.regions = Array.from(this.querySelectorAll('[data-loc-region]'));
    this.entries = this.buildIndex();

    if (!this.entries.length) return;

    if (this.variant === 'desktop' && this.panels.length) {
      this.setupRegions();
    }
    this.setupSearch();

    // Styling hook: panels only collapse into one-at-a-time view once the
    // behaviour that switches between them is actually running.
    this.dataset.enhanced = 'true';
  }

  buildIndex() {
    return Array.from(this.querySelectorAll('a[data-loc-name]')).map((link) => {
      const name = link.dataset.locName || link.textContent.trim();
      const parent = link.dataset.locParent || '';
      return {
        name,
        parent,
        url: link.getAttribute('href'),
        needle: `${name} ${parent}`.toLowerCase(),
      };
    });
  }

  setupRegions() {
    const details = this.closest('details');
    this.regions.forEach((region) => {
      const activate = () => this.activateRegion(region.dataset.locRegion);
      region.addEventListener('mouseenter', activate);
      region.addEventListener('focus', activate);
    });

    this.activateRegion(this.regions[0]?.dataset.locRegion);

    // Reopening the menu should start from the top rather than wherever the
    // pointer happened to leave it.
    details?.addEventListener('toggle', () => {
      if (!details.open) return;
      this.clearSearch();
      this.activateRegion(this.regions[0]?.dataset.locRegion);
    });
  }

  activateRegion(regionId) {
    if (!regionId) return;
    this.activeRegion = regionId;
    this.panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.locPanel === regionId);
    });
    this.regions.forEach((region) => {
      const isActive = region.dataset.locRegion === regionId;
      region.classList.toggle('is-active', isActive);
      region.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }

  setupSearch() {
    if (!this.searchInput || !this.resultsEl) return;

    this.searchInput.addEventListener('input', () => {
      this.renderResults(this.searchInput.value.trim());
    });

    this.searchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.clearSearch();
        event.stopPropagation();
        return;
      }
      if (event.key === 'ArrowDown') {
        const first = this.resultsEl.querySelector('a');
        if (first) {
          first.focus();
          event.preventDefault();
        }
      }
      if (event.key === 'Enter') {
        const first = this.resultsEl.querySelector('a');
        if (first) {
          window.location.href = first.href;
          event.preventDefault();
        }
      }
    });
  }

  renderResults(term) {
    const searching = term.length >= 2;
    this.classList.toggle('is-searching', searching);
    this.resultsEl.hidden = !searching;
    this.panels.forEach((panel) => panel.classList.toggle('is-muted', searching));

    if (!searching) {
      this.resultsEl.innerHTML = '';
      return;
    }

    const needle = term.toLowerCase();
    // Prefix matches first: typing "or" should offer Oregon before Florida.
    const matches = this.entries
      .filter((entry) => entry.needle.includes(needle))
      .sort((a, b) => {
        const aStarts = a.name.toLowerCase().startsWith(needle) ? 0 : 1;
        const bStarts = b.name.toLowerCase().startsWith(needle) ? 0 : 1;
        return aStarts - bStarts || a.name.localeCompare(b.name);
      })
      .slice(0, 12);

    if (!matches.length) {
      this.resultsEl.innerHTML = `
        <p class="location-menu__no-results">
          No destinations match “${this.escapeHtml(term)}”.
          <a class="link" href="/search?q=${encodeURIComponent(term)}&type=product">Search all prints</a>
        </p>`;
      return;
    }

    this.resultsEl.innerHTML = `
      <ul class="location-menu__results" role="list">
        ${matches
          .map(
            (entry) => `
              <li>
                <a href="${this.escapeAttr(entry.url)}" class="location-menu__result">
                  <span class="location-menu__result-name">${this.escapeHtml(entry.name)}</span>
                  ${entry.parent
                    ? `<span class="location-menu__result-parent">${this.escapeHtml(entry.parent)}</span>`
                    : ''}
                </a>
              </li>`
          )
          .join('')}
      </ul>`;
  }

  clearSearch() {
    if (!this.searchInput) return;
    this.searchInput.value = '';
    this.renderResults('');
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  escapeAttr(str) {
    return this.escapeHtml(str).replace(/'/g, '&#39;');
  }
}

if (!customElements.get('location-mega-menu')) {
  customElements.define('location-mega-menu', LocationMegaMenu);
}
